import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Lock, LogIn, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { errorMessage } from '../../api/client';
import { BRAND_NAME } from '../../utils/brand';
import { isTrustedAbsoluteUrl } from '../../utils/redirect';
import LoginOtpStep from '../../components/LoginOtpStep';

const MIN_LOCAL_DIGITS = 9;
const MAX_LOCAL_DIGITS = 10;

// USER sign-in — phone + password. OTP is only used for signup and
// forgot-password (client 2026-07-31).
function Login() {
  const { signInUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const from = params.get('from') || '/dashboard';

  // Absolute-URL `?from=` support so the landing page can bring users
  // back to itself after login (guarded by isTrustedAbsoluteUrl).
  function goTo(target) {
    if (isTrustedAbsoluteUrl(target)) {
      window.location.href = target;
    } else {
      navigate(target, { replace: true });
    }
  }

  // Preserve `?from=` when linking out to Register / Forgot-password so the
  // deep-link context (e.g. `from=/referrals` when the user clicked "Get my
  // link" on the landing page) survives the entire auth flow. Only forward
  // the param when it exists — no need to append `?from=/dashboard`.
  const fromSuffix = params.get('from')
    ? `?from=${encodeURIComponent(params.get('from'))}`
    : '';

  const [localDigits, setLocalDigits] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  // MFA step-2 challenge — set when the backend returns { requiresOtp }.
  // While non-null we render the OTP entry pane instead of the password form.
  const [otpChallenge, setOtpChallenge] = useState(null);

  function handleDigits(e) {
    let v = e.target.value.replace(/\D+/g, '');
    if (v.startsWith('0')) v = v.slice(1);
    setLocalDigits(v.slice(0, MAX_LOCAL_DIGITS));
  }

  function fullPhone() {
    return `+60${localDigits}`;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr('');
    if (localDigits.length < MIN_LOCAL_DIGITS) {
      setErr(`Enter a valid Malaysian mobile number (${MIN_LOCAL_DIGITS}–${MAX_LOCAL_DIGITS} digits after +60).`);
      return;
    }
    if (!password) {
      setErr('Enter your password.');
      return;
    }
    setSubmitting(true);
    try {
      const data = await signInUser(fullPhone(), password);
      if (data?.requiresOtp) {
        setOtpChallenge(data);
        toast.success(`Verification code sent to ${data.to}`);
      } else {
        toast.success('Welcome back');
        goTo(from);
      }
    } catch (e1) {
      setErr(errorMessage(e1));
    } finally {
      setSubmitting(false);
    }
  }

  if (otpChallenge) {
    return (
      <div className="auth-card">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--s-4)' }}>
          <img src="/fullLogo.png" alt={BRAND_NAME} style={{ height: 48, width: 'auto' }} draggable="false" />
        </div>
        <LoginOtpStep
          challenge={otpChallenge}
          onSuccess={() => { toast.success('Welcome back'); goTo(from); }}
          onBack={() => { setOtpChallenge(null); setErr(''); }}
          onResend={async () => {
            const data = await signInUser(fullPhone(), password);
            if (data?.requiresOtp) setOtpChallenge(data);
          }}
        />
      </div>
    );
  }

  return (
    <div className="auth-card">
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--s-4)' }}>
        <img src="/fullLogo.png" alt={BRAND_NAME} style={{ height: 48, width: 'auto' }} draggable="false" />
      </div>
      <h2>Member Login</h2>
      <div className="auth-sub">Access your health records and consultations.</div>

      <form onSubmit={onSubmit}>
        <div className="field">
          <label>Mobile number</label>
          <div
            className="row"
            style={{
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '8px 12px',
              background: 'var(--surface)',
              gap: 8,
            }}
          >
            <span style={{ color: 'var(--ink-600)', fontWeight: 600 }}>+60</span>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="tel"
              value={localDigits}
              onChange={handleDigits}
              placeholder="123456789"
              style={{ flex: 1, border: 0, outline: 'none', background: 'transparent', fontSize: 16 }}
              required
            />
          </div>
        </div>

        <div className="field">
          <label>Password</label>
          <div
            className="row"
            style={{
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '8px 12px',
              background: 'var(--surface)',
              gap: 8,
            }}
          >
            <input
              type={showPw ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              style={{ flex: 1, border: 0, outline: 'none', background: 'transparent', fontSize: 16 }}
              required
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--ink-500)' }}
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {err && <div className="notice warning" style={{ marginBottom: 'var(--s-4)' }}>{err}</div>}

        <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
          {submitting ? <span className="spinner" /> : <LogIn size={16} />}
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <div className="row between" style={{ marginTop: 'var(--s-3)' }}>
          <Link
            to={`/user-forgot-password${fromSuffix}`}
            className="text-sm"
            style={{ color: 'var(--brand-700)', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Lock size={12} /> Forgot password?
          </Link>
        </div>
      </form>

      <div className="divider" />
      {/* Patient-only sign-in page — deliberately no cross-links to the
          clinic portal. Clinic staff reach their portal via their own
          bookmarked /portal-login URL (client 2026-09-02). */}
      <div className="text-sm muted" style={{ textAlign: 'center' }}>
        New here? <Link to={`/register${fromSuffix}`}>Create an account</Link>
      </div>
    </div>
  );
}

export default Login;
